import { authFetch } from '../../api';

const API = 'https://hs.aida-advisory.com';

const fmtDOB    = d => { if (!d) return '—'; const p = d.split('-'); return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : d; };
const eligLabel = s => ({ eligible: 'Eligible', at_risk: 'At Risk', ineligible: 'Ineligible' }[s] || 'Unknown');
const or        = v => (v !== null && v !== undefined && v !== '') ? v : '—';

const gradeColor = g => {
  if (g === null || g === undefined || g === '') return '#9E9E9E';
  if (g >= 8) return '#00C853'; if (g >= 7) return '#66BB6A';
  if (g >= 6) return '#FFD600'; if (g >= 4) return '#FFB300';
  return '#EF5350';
};
const gradeBg = g => {
  if (g === null || g === undefined || g === '') return '#F5F5F5';
  if (g >= 8) return '#E8F5E9'; if (g >= 7) return '#F1F8E9';
  if (g >= 6) return '#FFFDE7'; if (g >= 4) return '#FFF8E1';
  return '#FFEBEE';
};

const DIM_COLORS = {
  'Character':            '#7E57C2',
  'Athletic Ability':     '#00C853',
  'Position Performance': '#FFB300',
  'Projection':           '#00B8D9',
  'Academic Profile':     '#78909C',
};
const dimColor = name => DIM_COLORS[name] || '#00B8D9';
const DIM_KEY_NAMES = {
  schemeFit: 'Scheme Fit', skillFit: 'Skill Fit', physicalFit: 'Physical Fit',
  mentalFit: 'Mental Fit', contextFit: 'Context Fit',
  Character: 'Character', 'Athletic Ability': 'Athletic Ability',
  'Position Performance': 'Position Performance', Projection: 'Projection',
  'Academic Profile': 'Academic Profile',
};
const dimDisplayName = key => DIM_KEY_NAMES[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());

export const generateDetailedReport = async (player, fitScore) => {
  const [hierRes, gradesRes, notesRes, gameLogsRes, totalsRes, statDefsRes] = await Promise.all([
    authFetch(`${API}/api/criteria/hierarchy?position=${player.position}`),
    authFetch(`${API}/api/grades/player/${player.id}`),
    authFetch(`${API}/api/criteria/category-notes/${player.id}`),
    authFetch(`${API}/api/game-logs/${player.id}`),
    authFetch(`${API}/api/player-stats/${player.id}/totals`),
    authFetch(`${API}/api/stat-definitions/${player.position}`),
  ]);

  const categories = hierRes.ok  ? await hierRes.json()  : [];
  const gradesData = gradesRes.ok ? await gradesRes.json() : [];
  const notesData  = notesRes.ok  ? await notesRes.json()  : [];
  const gameLogs   = gameLogsRes.ok ? await gameLogsRes.json() : [];
  const totals     = totalsRes.ok ? await totalsRes.json() : null;
  const statDefsData = statDefsRes.ok ? await statDefsRes.json() : { stats: [] };
  const statDefs   = statDefsData.stats || [];

  const grades = {};
  let scoutName = '';
  gradesData.forEach(g => {
    grades[String(g.criterion_id)] = g.grade;
    if (g.scout_name && !scoutName) scoutName = g.scout_name;
  });
  const notes = {};
  notesData.forEach(n => { notes[n.category_id] = n.notes; });

  const fitPct = fitScore?.overall > 0 ? ((fitScore.overall / 9) * 100).toFixed(1) + '%' : 'N/A';
  const today  = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const dimBars = fitScore?.dimensionScores
    ? Object.entries(fitScore.dimensionScores).map(([name, score]) => {
        const displayName = dimDisplayName(name);
        const col = dimColor(displayName);
        const pct = Math.round((score / 9) * 100);
        return `<div class="bar-row">
          <div class="bar-label">${displayName}</div>
          <div class="bar-track"><div class="bar-fill" style="width:${pct}%;background:${col}"></div></div>
          <div class="bar-score" style="color:${col}">${score.toFixed(1)}</div>
        </div>`;
      }).join('') : '';

  const getText = item => typeof item === 'string' ? item : (item?.name || item?.text || item?.label || item?.description || '');
  const strengthsHtml    = (fitScore?.strengths    || []).map(s => `<div class="si-item strength-item">${getText(s)}</div>`).join('');
  const improvementsHtml = (fitScore?.improvements || []).map(s => `<div class="si-item improvement-item">${getText(s)}</div>`).join('');
  const highlightsHtml   = (fitScore?.highlights   || []).slice(0,3).map(h => `<div class="highlight-item">&#x203A; ${getText(h)}</div>`).join('');

  const gradesHtml = categories.map(cat => {
    const catGrades = (cat.sub_criteria || []).map(c => grades[String(c.id)]).filter(g => g !== undefined && g !== '' && g !== null);
    const catAvg    = catGrades.length > 0 ? catGrades.reduce((a,b) => a+b, 0) / catGrades.length : null;

    const subRows = (cat.sub_criteria || []).map(c => {
      const g = grades[String(c.id)];
      const has = g !== undefined && g !== '' && g !== null;
      return `<tr class="sub-row">
        <td class="sub-name">${c.name}</td>
        <td class="sub-grade" style="color:${has ? gradeColor(g) : '#BDBDBD'};font-weight:${has ? '700' : '400'}">${has ? g : '—'}</td>
        <td class="grade-bar-cell">${has ? `<div class="inline-bar"><div class="inline-fill" style="width:${Math.round((g/9)*100)}%;background:${gradeColor(g)}"></div></div>` : ''}</td>
      </tr>`;
    }).join('');

    const noteHtml = notes[cat.id] ? `<tr class="note-row"><td colspan="3">
      <div class="evaluator-note">
        <span class="note-label">EVALUATOR NOTES</span>
        <span class="note-text">${notes[cat.id]}</span>
      </div></td></tr>` : '';

    return `<div class="cat-block">
      <table class="grades-table">
        <thead><tr class="cat-header-row">
          <th class="cat-name">${cat.name}</th>
          <th class="cat-avg" style="color:${catAvg !== null ? gradeColor(catAvg) : '#9E9E9E'};background:${catAvg !== null ? gradeBg(catAvg) : '#F5F5F5'}">
            ${catAvg !== null ? 'AVG &nbsp;' + catAvg.toFixed(1) : 'NOT GRADED'}
          </th>
          <th class="cat-bar-th"></th>
        </tr></thead>
        <tbody>${subRows}${noteHtml}</tbody>
      </table>
    </div>`;
  }).join('');

  // --- Build Game Stats HTML ---
  let gameStatsHtml = '';
  if (gameLogs.length > 0) {
    const inputDefs = statDefs.filter(d => d.stat_type === 'input');
    const displayDefs = inputDefs.slice(0, 8); // Show up to 8 stat columns

    // Header row
    const headerCells = displayDefs.map(d => `<th style="padding:6px 8px;font-size:9px;color:#546E7A;text-transform:uppercase;letter-spacing:.05em;text-align:center;border-bottom:2px solid #D0D8E8">${d.display_name}</th>`).join('');

    // Game rows
    const gameRows = gameLogs.map(log => {
      const statusColor = { starter: '#00C853', backup: '#FFB300', injured: '#EF5350' }[log.player_status] || '#9E9E9E';
      const statusBg = { starter: '#E8F5E9', backup: '#FFF8E1', injured: '#FFEBEE' }[log.player_status] || '#F5F5F5';
      const haLabel = log.is_home ? 'H' : 'A';
      const statCells = displayDefs.map(d => {
        const val = log.stats[d.stat_key];
        return `<td style="padding:5px 8px;text-align:center;font-size:12px;border-bottom:1px solid #EEF2F8">${val !== undefined && val !== null ? (val === Math.floor(val) ? val : val.toFixed(1)) : '—'}</td>`;
      }).join('');

      return `<tr>
        <td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #EEF2F8;white-space:nowrap">${log.game_date || '—'}</td>
        <td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #EEF2F8">
          <div>${log.opponent_name || '—'}</div>
          ${log.opponent_location ? `<div style="color:#9E9E9E;font-size:9px">${log.opponent_location}</div>` : ''}
        </td>
        <td style="padding:5px 8px;text-align:center;font-size:11px;border-bottom:1px solid #EEF2F8">${haLabel}</td>
        <td style="padding:5px 8px;text-align:center;border-bottom:1px solid #EEF2F8">
          <span style="font-size:10px;padding:2px 6px;border-radius:3px;background:${statusBg};color:${statusColor};font-weight:600">${log.player_status}</span>
        </td>
        ${statCells}
      </tr>`;
    }).join('');

    // Totals row
    const totalCells = displayDefs.map(d => {
      const val = totals?.totals?.[d.stat_key];
      return `<td style="padding:6px 8px;text-align:center;font-size:12px;font-weight:700;color:#1A2E4A;border-top:2px solid #1A2E4A">${val !== undefined && val !== null ? (val === Math.floor(val) ? val : val.toFixed(1)) : '—'}</td>`;
    }).join('');

    // Injury count
    const injuryCount = gameLogs.filter(l => l.player_status === 'injured').length;
    const injuryNote = injuryCount > 0
      ? `<div style="margin-top:8px;padding:6px 12px;background:#FFEBEE;border-left:3px solid #EF5350;border-radius:0 4px 4px 0;font-size:11px;color:#C62828">
           <strong>Injury Note:</strong> Player was listed as injured for ${injuryCount} of ${gameLogs.length} games (${Math.round(injuryCount/gameLogs.length*100)}%)
         </div>`
      : '';

    // Calculated stats row
    const calcDefs = statDefs.filter(d => d.stat_type === 'calculated');
    let calcHtml = '';
    if (calcDefs.length > 0 && totals?.calculated) {
      const calcItems = calcDefs
        .filter(d => totals.calculated[d.stat_key] !== undefined)
        .map(d => `<span style="margin-right:16px"><strong>${d.display_name}:</strong> <span style="color:#00C853;font-weight:700">${totals.calculated[d.stat_key]}</span></span>`);
      if (calcItems.length > 0) {
        calcHtml = `<div style="margin-top:8px;padding:6px 12px;background:#F0FBF4;border-left:3px solid #00C853;border-radius:0 4px 4px 0;font-size:11px;color:#1A3A1A">
          <strong>Calculated:</strong> ${calcItems.join('')}
        </div>`;
      }
    }

    gameStatsHtml = `
      <div style="page-break-inside:avoid;break-inside:avoid">
      <div class="section-hdr" style="margin-top:28px"><div class="section-hdr-bar" style="background:#0D47A1"></div><div class="section-hdr-title">Game Statistics</div></div>
      <div style="font-size:9px;color:#9E9E9E;margin-bottom:8px;font-style:italic">Stats are manually entered by coaching staff and should be independently verified.</div>
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr>
            <th style="padding:6px 8px;font-size:9px;color:#546E7A;text-transform:uppercase;letter-spacing:.05em;text-align:left;border-bottom:2px solid #D0D8E8">Date</th>
            <th style="padding:6px 8px;font-size:9px;color:#546E7A;text-transform:uppercase;letter-spacing:.05em;text-align:left;border-bottom:2px solid #D0D8E8">Opponent</th>
            <th style="padding:6px 8px;font-size:9px;color:#546E7A;text-transform:uppercase;letter-spacing:.05em;text-align:center;border-bottom:2px solid #D0D8E8">H/A</th>
            <th style="padding:6px 8px;font-size:9px;color:#546E7A;text-transform:uppercase;letter-spacing:.05em;text-align:center;border-bottom:2px solid #D0D8E8">Status</th>
            ${headerCells}
          </tr>
        </thead>
        <tbody>
          ${gameRows}
          <tr style="background:#EDF2FA">
            <td colspan="4" style="padding:6px 8px;font-size:11px;font-weight:800;color:#1A2E4A;border-top:2px solid #1A2E4A">
              TOTALS (${gameLogs.length} game${gameLogs.length !== 1 ? 's' : ''})
            </td>
            ${totalCells}
          </tr>
        </tbody>
      </table>
      ${calcHtml}
      ${injuryNote}
      </div>
    `;
  }

  const html = `<!DOCTYPE html><html><head>
<meta charset="UTF-8"/>
<title>Scout Report &#x2014; ${player.name}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Inter',Arial,sans-serif;background:#f0f4f8;color:#1a1a2e;font-size:13px;}
.page{width:850px;margin:0 auto;background:white;}

/* COVER */
.cover-header{background:linear-gradient(135deg,#0D1E36 0%,#0A1628 100%);padding:36px 48px 32px;border-top:4px solid #00D4FF;display:flex;justify-content:space-between;align-items:flex-start;gap:24px;}
.cover-left{flex:1;}
.cover-org{color:#546E7A;font-size:10px;letter-spacing:.15em;text-transform:uppercase;margin-bottom:10px;}
.cover-name{color:#FFF;font-size:42px;font-weight:800;line-height:1;letter-spacing:-.02em;margin-bottom:10px;}
.cover-meta{color:#00D4FF;font-size:14px;font-weight:700;margin-bottom:6px;letter-spacing:.03em;}
.cover-location{color:#B0BEC5;font-size:12px;}
.cover-right{text-align:right;flex-shrink:0;}
.cover-fit-label{color:#546E7A;font-size:9px;text-transform:uppercase;letter-spacing:.12em;margin-bottom:4px;}
.cover-fit-score{color:#00E676;font-size:44px;font-weight:800;line-height:1;white-space:nowrap;letter-spacing:-.02em;}
.cover-fit-tier{color:#00E676;font-size:13px;font-weight:700;margin-top:4px;letter-spacing:.06em;}
.cover-highlights{margin-top:10px;}
.highlight-item{color:#7DC8A0;font-size:11px;margin-top:3px;}

/* STATS STRIP */
.stats-strip{background:#1A2E4A;display:flex;border-bottom:1px solid rgba(84,110,122,.3);}
.stat-chip{flex:1;text-align:center;padding:12px 8px;border-right:1px solid rgba(84,110,122,.2);}
.stat-chip:last-child{border-right:none;}
.stat-chip-label{color:#546E7A;font-size:9px;text-transform:uppercase;letter-spacing:.1em;margin-bottom:4px;}
.stat-chip-value{color:#00D4FF;font-size:16px;font-weight:700;}

/* BODY */
.body{padding:32px 48px 48px;}
.section-hdr{display:flex;align-items:center;gap:10px;margin:28px 0 14px;}
.section-hdr-bar{width:4px;border-radius:2px;height:20px;flex-shrink:0;}
.section-hdr-title{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.12em;color:#1A2E4A;}

/* INFO TABLE */
.info-table{width:100%;border-collapse:collapse;}
.info-table td{padding:8px 12px;border:1px solid #E0E8F0;font-size:13px;}
.info-table .lbl{background:#EDF2FA;font-weight:700;color:#1A3060;width:20%;}
.info-table .val{color:#2A2A2A;width:30%;}

/* BARS */
.bar-row{display:flex;align-items:center;gap:12px;margin-bottom:8px;}
.bar-label{width:200px;font-size:12px;color:#546E7A;flex-shrink:0;}
.bar-track{flex:1;height:12px;background:#D4DCE8;border-radius:6px;overflow:hidden;}
.bar-fill{height:100%;border-radius:6px;}
.bar-score{width:36px;text-align:right;font-size:15px;font-weight:700;flex-shrink:0;}

/* SI */
.si-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:4px;}
.si-col-title{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.1em;margin-bottom:10px;}
.si-item{padding:7px 12px 7px 14px;border-radius:5px;font-size:12px;margin-bottom:6px;border-left:3px solid;}
.strength-item{background:#F0FBF4;border-color:#00C853;color:#1A3A1A;}
.improvement-item{background:#FFF8F0;border-color:#FFB300;color:#3A2A00;}

/* GRADES */
.cat-block{margin-bottom:16px;page-break-inside:avoid;break-inside:avoid;}
.grades-table{width:100%;border-collapse:collapse;}
.cat-header-row th{padding:9px 14px;border:1px solid #D0D8E8;}
.cat-name{background:#EDF2FA;color:#1A3060;font-size:13px;font-weight:800;text-align:left;width:65%;}
.cat-avg{font-size:13px;font-weight:800;text-align:center;width:12%;}
.cat-bar-th{background:#EDF2FA;width:23%;}
.sub-row td{padding:6px 14px;border-bottom:1px solid #EEF2F8;border-left:1px solid #D0D8E8;border-right:1px solid #D0D8E8;}
.sub-name{color:#3A3A5A;font-size:12px;padding-left:24px!important;}
.sub-grade{text-align:center;font-size:14px;}
.grade-bar-cell{padding:6px 14px!important;}
.inline-bar{height:8px;background:#E8EEF6;border-radius:4px;overflow:hidden;}
.inline-fill{height:100%;border-radius:4px;}
.note-row td{border-left:1px solid #D0D8E8;border-right:1px solid #D0D8E8;border-bottom:1px solid #D0D8E8;padding:8px 14px;}
.evaluator-note{background:#F0F8FF;border-left:3px solid #00B8D9;padding:8px 14px;border-radius:0 4px 4px 0;}
.note-label{font-size:10px;font-weight:800;color:#00B8D9;text-transform:uppercase;letter-spacing:.1em;margin-right:10px;}
.note-text{font-size:12px;color:#444;font-style:italic;}

/* FOOTER */
.report-footer{margin-top:40px;padding-top:14px;border-top:1px solid #D0D8E8;display:flex;justify-content:space-between;font-size:11px;color:#999;}

/* PRINT */
@media print{
  body{background:white;}
  .page{width:100%;margin:0;}
  .cover-header,.stats-strip,.bar-fill,.inline-fill,.si-item,.info-table .lbl,.cat-name,.cat-bar-th,.evaluator-note{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
  .bar-row,.si-item,.info-table tr,.sub-row,.cat-block,.section-hdr{page-break-inside:avoid;break-inside:avoid;}
  .cover-header,.stats-strip{page-break-after:avoid;break-after:avoid;}
  @page{margin:0.5in;size:letter;}
}
</style></head><body>
<div class="page">

<div class="cover-header">
  <div class="cover-left">
    <div class="cover-org">SchemeSync HS &#x2014; Prospect Evaluation Report</div>
    <div class="cover-name">${player.name}</div>
    <div class="cover-meta">${[player.position, player.class_year, player.high_school].filter(Boolean).join('  \u2022  ')}</div>
    <div class="cover-location">${[player.hometown, player.hs_state].filter(Boolean).join(', ')}</div>
  </div>
  <div class="cover-right">
    <div class="cover-fit-label">Program Fit Score</div>
    <div class="cover-fit-score">${fitPct}</div>
    <div class="cover-fit-tier">${fitScore?.tier || ''}</div>
    <div class="cover-highlights">${highlightsHtml}</div>
  </div>
</div>

<div class="stats-strip">
  ${[
    {l:'HT',    v: or(player.height)},
    {l:'WT',    v: player.weight ? player.weight+' lbs' : '—'},
    {l:'GPA',   v: player.gpa ? parseFloat(player.gpa).toFixed(2) : '—', color: player.gpa ? (parseFloat(player.gpa)>=3.5?'#00E676':parseFloat(player.gpa)>=2.5?'#FFD600':'#EF5350') : '#546E7A'},
    {l:'40',    v: player.forty_time ? player.forty_time+'s' : '—'},
    {l:'VERT',  v: player.vertical_jump ? player.vertical_jump+'"' : '—'},
    {l:'STARS', v: player.recruit_stars ? player.recruit_stars+'&#x2605;' : '—'},
  ].map(s=>`<div class="stat-chip"><div class="stat-chip-label">${s.l}</div><div class="stat-chip-value" style="color:${s.color||'#00D4FF'}">${s.v}</div></div>`).join('')}
</div>

<div class="body">

<div class="section-hdr"><div class="section-hdr-bar" style="background:#1A3060"></div><div class="section-hdr-title">Player Profile</div></div>
<table class="info-table">
  <tr><td class="lbl">Position</td><td class="val">${or(player.position)}</td><td class="lbl">Class Year</td><td class="val">${or(player.class_year)}</td></tr>
  <tr><td class="lbl">Height</td><td class="val">${or(player.height)}</td><td class="lbl">Weight</td><td class="val">${player.weight ? player.weight+' lbs' : '—'}</td></tr>
  <tr><td class="lbl">Date of Birth</td><td class="val">${fmtDOB(player.dob)}</td><td class="lbl">Grad Year</td><td class="val">${or(player.graduation_year)}</td></tr>
  <tr><td class="lbl">High School</td><td class="val">${or(player.high_school)}</td><td class="lbl">Hometown</td><td class="val">${[player.hometown,player.hs_state].filter(Boolean).join(', ')||'—'}</td></tr>
</table>

<div class="section-hdr"><div class="section-hdr-bar" style="background:#1A4A2E"></div><div class="section-hdr-title">Academic Profile</div></div>
<table class="info-table">
  <tr>
    <td class="lbl">GPA</td>
    <td class="val" style="color:${player.gpa ? (parseFloat(player.gpa)>=3.5?'#00C853':parseFloat(player.gpa)>=2.5?'#FFD600':'#EF5350') : '#9E9E9E'};font-weight:700;font-size:15px">${player.gpa ? parseFloat(player.gpa).toFixed(2) : '—'}</td>
    <td class="lbl">Eligibility</td>
    <td class="val" style="color:${{eligible:'#00C853',at_risk:'#FFB300',ineligible:'#EF5350'}[player.ncaa_eligibility]||'#9E9E9E'};font-weight:600">${eligLabel(player.ncaa_eligibility)}</td>
  </tr>
  <tr><td class="lbl">SAT Score</td><td class="val">${or(player.sat_score)}</td><td class="lbl">ACT Score</td><td class="val">${or(player.act_score)}</td></tr>
</table>

<div class="section-hdr"><div class="section-hdr-bar" style="background:#4A3000"></div><div class="section-hdr-title">Athletic Measurables</div></div>
<table class="info-table">
  <tr><td class="lbl">40-Yard Dash</td><td class="val">${player.forty_time ? player.forty_time+'s' : '—'}</td><td class="lbl">Vertical Jump</td><td class="val">${player.vertical_jump ? player.vertical_jump+'"' : '—'}</td></tr>
  <tr><td class="lbl">Bench Press</td><td class="val">${player.bench_press ? player.bench_press+' reps' : '—'}</td><td class="lbl">Recruit Stars</td><td class="val">${player.recruit_stars ? player.recruit_stars+'&#x2605;' : '—'}</td></tr>
  <tr><td class="lbl">Recruit Rating</td><td class="val">${or(player.recruit_rating)}</td><td class="lbl">MaxPreps</td><td class="val">${player.maxpreps_url ? '<a href="'+player.maxpreps_url+'" style="color:#00B8D9">View Profile</a>' : 'Not linked'}</td></tr>
</table>

${gameStatsHtml}

${fitScore?.overall > 0 ? `
<div style="page-break-inside:avoid;break-inside:avoid"><div class="section-hdr"><div class="section-hdr-bar" style="background:#1A2744"></div><div class="section-hdr-title">Program Fit Score Breakdown</div></div>
${dimBars}</div>
${(fitScore?.strengths?.length > 0 || fitScore?.improvements?.length > 0) ? `
<div class="section-hdr" style="margin-top:24px"><div class="section-hdr-bar" style="background:#546E7A"></div><div class="section-hdr-title">Strengths &amp; Development Areas</div></div>
<div class="si-grid">
  <div><div class="si-col-title" style="color:#00C853">Top Strengths</div>${strengthsHtml}</div>
  <div><div class="si-col-title" style="color:#E65100">Areas for Improvement</div>${improvementsHtml}</div>
</div>` : ''}
` : ''}

${categories.length > 0 ? `
<div class="section-hdr" style="margin-top:28px"><div class="section-hdr-bar" style="background:#1A2744"></div><div class="section-hdr-title">Detailed Evaluation Grades</div></div>
${gradesHtml}` : ''}

<div class="report-footer">
  <span>Evaluated by: <strong>${scoutName || 'SchemeSync HS'}</strong></span>
  <span>Generated: ${today}</span>
  <span>SchemeSync HS Scouting Platform</span>
</div>

</div></div>
<script>window.onload = () => window.print();</script>
</body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
};
